/**
 * <al-chat>: chat del asistente ActivaLab (snippets/whatsapp-button.liquid).
 * Habla con activalab-bot (/api/web-chat), el mismo agente del WhatsApp.
 * Sin JS el botón es un enlace normal a WhatsApp.
 */
(() => {
  if (customElements.get('al-chat')) return;

  const SALUDO =
    '¡Hola! 👋 Soy el asistente de ActivaLab. Te ayudo a elegir productos, armar tu compra, ver costos de envío o revisar tu pedido. ¿En qué te ayudo?';
  const ERROR = 'Tuve un problema para responderte 😕. Si quieres, sigue por WhatsApp y te atiende una persona.';
  const K_SESION = 'al-chat-session';
  const K_LOG = 'al-chat-log';
  // Chrome restaura el sessionStorage al reabrir pestañas: sin vencimiento, una
  // conversación vieja reaparecía días después. Tras 30 min sin actividad se
  // empieza de cero, con sesión nueva (así el bot tampoco recuerda la anterior).
  const VENCE_MS = 30 * 60 * 1000;

  const leer = (store, k) => {
    try {
      return store.getItem(k);
    } catch {
      return null;
    }
  };
  const guardar = (store, k, v) => {
    try {
      store.setItem(k, v);
    } catch {}
  };

  function uuid() {
    if (crypto.randomUUID) return crypto.randomUUID();
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
    );
  }

  // Texto del bot → HTML seguro: se escapa todo y recién después se arman
  // los links (solo http/https) y la *negrita* de un asterisco.
  function formatear(texto) {
    const esc = String(texto)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    return esc
      .replace(/(?:https?:\/\/|\b(?=activalab\.cl\/))[^\s<]+[^\s<.,;:!?)]/g, (url) => {
        const href = /^https?:/.test(url) ? url : `https://${url}`;
        return `<a href="${href}" target="_blank" rel="noopener">${url.replace(/^https?:\/\//, '')}</a>`;
      })
      .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>');
  }

  class AlChat extends HTMLElement {
    connectedCallback() {
      this.endpoint = this.dataset.endpoint;
      this.wa = this.dataset.wa;
      this.toggleBtn = this.querySelector('[data-chat-toggle]');
      this.panel = this.querySelector('.al-chat');
      this.logEl = this.querySelector('[data-chat-log]');
      this.quick = this.querySelector('[data-chat-quick]');
      this.form = this.querySelector('[data-chat-form]');
      this.input = this.form.querySelector('input');
      this.sendBtn = this.form.querySelector('button');
      this.ocupado = false;

      let guardado = null;
      try {
        guardado = JSON.parse(leer(sessionStorage, K_LOG) || 'null');
      } catch {}
      const vigente = guardado && Array.isArray(guardado.log) && Date.now() - guardado.ts < VENCE_MS;
      this.log = vigente ? guardado.log : [];
      this.sesion = vigente ? leer(localStorage, K_SESION) : null;
      if (!this.sesion) {
        this.sesion = uuid();
        guardar(localStorage, K_SESION, this.sesion);
        guardar(sessionStorage, K_LOG, JSON.stringify({ ts: Date.now(), log: [] }));
      }

      this.toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.hasAttribute('open') ? this.cerrar() : this.abrir();
      });
      this.querySelector('[data-chat-close]').addEventListener('click', () => this.cerrar());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.hasAttribute('open')) this.cerrar();
      });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.enviar(this.input.value);
      });
      this.quick.addEventListener('click', (e) => {
        if (e.target.tagName === 'BUTTON') this.enviar(e.target.textContent);
      });
    }

    abrir() {
      if (!this.pintado) {
        this.pintado = true;
        if (!this.log.length) this.log.push({ r: 'bot', t: SALUDO });
        this.log.forEach((m) => this.pintar(m));
        this.quick.hidden = this.log.some((m) => m.r === 'user');
      }
      this.setAttribute('open', '');
      this.panel.hidden = false;
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      this.logEl.scrollTop = this.logEl.scrollHeight;
      if (window.matchMedia('(min-width: 750px)').matches) this.input.focus();
    }

    cerrar() {
      this.removeAttribute('open');
      this.panel.hidden = true;
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      this.toggleBtn.focus();
    }

    pintar(m) {
      const div = document.createElement('div');
      div.className = `al-chat__msg al-chat__msg--${m.r}`;
      if (m.r === 'bot') div.innerHTML = formatear(m.t);
      else div.textContent = m.t;
      this.logEl.append(div);
      if (m.cart) this.boton(m.cart, 'Ir a pagar 🛒', false);
      if (m.wa) this.boton(m.wa, 'Continuar en WhatsApp', true);
      this.logEl.scrollTop = this.logEl.scrollHeight;
    }

    boton(href, texto, esWa) {
      const a = document.createElement('a');
      a.className = `al-chat__cta${esWa ? ' al-chat__cta--wa' : ''}`;
      a.href = href;
      a.textContent = texto;
      if (esWa) {
        a.target = '_blank';
        a.rel = 'noopener';
      }
      this.logEl.append(a);
    }

    agregar(m) {
      this.log.push(m);
      this.log = this.log.slice(-40);
      guardar(sessionStorage, K_LOG, JSON.stringify({ ts: Date.now(), log: this.log }));
      this.pintar(m);
    }

    async enviar(texto) {
      const msg = String(texto || '').trim().slice(0, 500);
      if (!msg || this.ocupado) return;
      this.ocupado = true;
      this.sendBtn.disabled = true;
      this.quick.hidden = true;
      this.input.value = '';
      this.agregar({ r: 'user', t: msg });

      const typing = document.createElement('div');
      typing.className = 'al-chat__typing';
      typing.setAttribute('aria-label', 'Escribiendo');
      typing.innerHTML = '<span></span><span></span><span></span>';
      this.logEl.append(typing);
      this.logEl.scrollTop = this.logEl.scrollHeight;

      let resp;
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 25000);
        const r = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: this.sesion,
            message: msg,
            page: { type: this.dataset.pageType || '', handle: this.dataset.handle || '' },
          }),
          signal: ctrl.signal,
        });
        clearTimeout(timer);
        resp = r.ok ? await r.json() : null;
      } catch {
        resp = null;
      }
      typing.remove();

      if (resp && resp.text) {
        const cart = resp.cartUrl && /^https:\/\/(www\.)?activalab\.cl\//.test(resp.cartUrl) ? resp.cartUrl : null;
        const wa = resp.handoff && /^https:\/\/wa\.me\//.test(resp.handoff.url) ? resp.handoff.url : null;
        this.agregar({ r: 'bot', t: resp.text, cart, wa });
      } else {
        this.agregar({ r: 'bot', t: ERROR, wa: this.wa });
      }

      this.ocupado = false;
      this.sendBtn.disabled = false;
      if (window.matchMedia('(min-width: 750px)').matches) this.input.focus();
    }
  }

  customElements.define('al-chat', AlChat);
})();
